import {
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Box,
  Button,
} from '@chakra-ui/react';

import { WARNING_CODE_EFFECTIVE_DATE_IN_PAST } from '../constants';

interface EffectiveDateWarningBannerProps {
  requestedDate: string;
  validFrom: string | null;
  validTo?: string | null;
  onNavigate: () => void;
  showNavigateButton?: boolean;
  warningCode?: string;
}

function formatDate(dateStr: string): string {
  const date = new Date(dateStr + 'T00:00:00');
  return date.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

function getLastValidDate(endDateStr: string): string {
  const [year, month, day] = endDateStr.split('-').map(Number);
  const date = new Date(year, month - 1, day - 1);
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function describeSupportedRange(
  validFrom: string | null,
  validTo: string | null | undefined,
): string {
  if (validFrom && validTo) {
    const lastValidDate = getLastValidDate(validTo);
    return ` Supported dates are ${formatDate(validFrom)} through ${formatDate(lastValidDate)}.`;
  }
  if (validFrom) {
    return ` The earliest supported date is ${formatDate(validFrom)}.`;
  }
  if (validTo) {
    const lastValidDate = getLastValidDate(validTo);
    return ` The latest supported date is ${formatDate(lastValidDate)}.`;
  }
  return '';
}

export const EffectiveDateWarningBanner = ({
  requestedDate,
  validFrom,
  validTo,
  onNavigate,
  showNavigateButton = true,
  warningCode,
}: EffectiveDateWarningBannerProps) => {
  const isPastDate = warningCode === WARNING_CODE_EFFECTIVE_DATE_IN_PAST;

  return (
    <Box mb={4}>
      <Alert status="warning" variant="subtle" borderRadius="md">
        <AlertIcon />
        <Box flex={1}>
          <AlertTitle>
            {isPastDate
              ? 'Effective Date In Past'
              : 'Effective Date Not Supported'}
          </AlertTitle>
          <AlertDescription>
            {isPastDate ? (
              <>
                The effective date ({formatDate(requestedDate)}) is in the past.
                Please select a date on or after today.
              </>
            ) : (
              <>
                The effective date ({formatDate(requestedDate)}) is not within
                the available quote period.
                {describeSupportedRange(validFrom, validTo)}
              </>
            )}
          </AlertDescription>
        </Box>
        {showNavigateButton && (
          <Button size="sm" colorScheme="orange" onClick={onNavigate}>
            Go to Policy Info
          </Button>
        )}
      </Alert>
    </Box>
  );
};
